"use client";

import { ReactNode, useMemo } from "react";

import { Badge } from "@/components/ui/badge";
import { useIsMobile } from "@/hooks/use-mobile";
import { cn, formatIsoStringDate } from "@/lib/utils";
import { t } from "@/lib/localization";

import { useGetLomdaV3 } from "@/features/storage/sheets/lomdaV3Sheet/hooks/useGetLomdaV3";
import { GetFilesResponseType } from "../../hooks/useGetFiles";
import LomdaV3Thumbnail from "./lomdaV3Thumbnail";

interface LomdaV3ListItemProps {
    file: GetFilesResponseType["data"][0];
    onClick?: () => void;
    options?: ReactNode;
    selected?: boolean;
}

export default function LomdaV3ListItem({
    file,
    onClick,
    options,
    selected,
}: LomdaV3ListItemProps) {
    const isMobile = useIsMobile();

    const { thumbnailWidth, titleMinWidth, optionsWidth } = useMemo(
        () => ({
            thumbnailWidth: isMobile ? 150 : 180,
            titleMinWidth: isMobile ? 140 : 200,
            optionsWidth: 50,
        }),
        [isMobile]
    );

    const badgeText =
        file.detailForBadge <= 1
            ? t("storage.lomda-v3-card.num-of-pages-badge.single")
            : file.detailForBadge +
              " " +
              t("storage.lomda-v3-card.num-of-pages-badge.multiple");

    const title = file.name || t("storage.no-name", undefined, undefined);
    const subtitle = `${t("storage.lomda-v3", undefined, undefined)} · ${formatIsoStringDate(file.date)}`;

    const gridCols = `${thumbnailWidth}px 1fr ${optionsWidth}px`;

    const { data } = useGetLomdaV3(true, file.id);

    return (
        <div
            onClick={onClick}
            className={cn(
                "group cursor-pointer bg-card hover:bg-accent transition",
                "grid items-center w-full p-4 gap-2 sm:gap-4 overflow-x-auto",
                selected && "border-s-4 border-s-primary"
            )}
            style={{ gridTemplateColumns: gridCols }}
        >
            <div
                className="aspect-video rounded-md relative flex-shrink-0 border overflow-hidden bg-muted"
                style={{ width: thumbnailWidth }}
            >
                <LomdaV3Thumbnail
                    lomda={data?.data}
                    lomdaId={file.id}
                    lomdaName={file.name ?? ""}
                />

                <Badge className="absolute bottom-2 left-2 font-normal px-2">
                    {badgeText}
                </Badge>
            </div>

            <div
                className="flex items-center gap-3"
                style={{ minWidth: titleMinWidth }}
            >
                <div className="flex flex-col gap-0.5 truncate">
                    <h3
                        className="text-base font-medium truncate"
                        title={title}
                    >
                        {title}
                    </h3>
                    <p className="text-sm text-muted-foreground truncate">
                        {subtitle}
                    </p>
                </div>
            </div>

            <div className="justify-self-end flex-shrink-0">{options}</div>
        </div>
    );
}
